import { type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';
import { FreeShippingBadge, type BadgeBackgroundColor } from './FreeShippingBadge';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import styles from './styles.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';

type ShippingInfoProps = {
    shippingPrices?: Record<string, number> | null;
    currency: string;
    backgroundColor?: BadgeBackgroundColor;
    reduceMargins?: boolean;
    mlFreightQuotesv2Eligible: boolean | null;
};

const VisibilityWrapper: FC<{
    children: ReactNode;
    mlFreightQuotesv2Eligible: boolean | null;
}> = ({ children, mlFreightQuotesv2Eligible }) => {
    return mlFreightQuotesv2Eligible ? (
        <VisibilityTracker
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackAbTestV2Variant('mlFreightQuotesv2');
                }
            }}
        >
            {children}
        </VisibilityTracker>
    ) : (
        <>{children}</>
    );
};
export const ShippingInfo: FC<ShippingInfoProps> = ({
    currency,
    backgroundColor,
    reduceMargins,
    shippingPrices,
    mlFreightQuotesv2Eligible,
}) => {
    // shippingPrices are not defined when tile is not supposed to have shipping cost at all
    // shippingPrices are null in two cases:
    // - are not yet loaded
    // - loading was skipped, e.g. for sold items
    if (!shippingPrices) {
        return null;
    }

    const amount = shippingPrices[currency] ?? null;

    return (
        <VisibilityWrapper mlFreightQuotesv2Eligible={mlFreightQuotesv2Eligible}>
            {amount === 0 ? (
                <FreeShippingBadge
                    backgroundColor={backgroundColor}
                    reduceMargins={reduceMargins}
                />
            ) : (
                <div
                    className={classnames(
                        reduceMargins ? dibsCss.mb2px : dibsCss.mbXsmall,
                        styles.negativeTopMargin
                    )}
                    data-tn="shipping-cost"
                >
                    {amount === null ? (
                        <FormattedMessage
                            id="dc.searchProductTile.shippingRatesVary"
                            defaultMessage="Shipping rates vary"
                        />
                    ) : (
                        <FormattedMessage
                            id="dc.searchProductTile.shippingPrice"
                            defaultMessage="{price} shipping"
                            values={{
                                price: <FormattedPrice currency={currency} value={amount} />,
                            }}
                        />
                    )}
                </div>
            )}
        </VisibilityWrapper>
    );
};
